<template>
    <div>
        <div
            class="flex"
            :class="'sm' === size ? 'gap-1 sm:gap-2' : 'gap-2 lg:gap-3'"
        >
            <button
                class="rounded-md ring-1 ring-inset ring-grind-200 transition hover:ring-3 hover:ring-grind"
                :class="
                    'sm' === size
                        ? 'px-1.5 hover:ring-2 sm:px-3'
                        : 'px-5 hover:ring-3'
                "
                aria-label="Decrement quantity"
                data-cy="minus-btn"
                :disabled="minDisabled"
                @click.prevent="onMinus"
            >
                <Icon
                    name="minus"
                    :class="{ 'text-2xs sm:text-sm': 'sm' === size }"
                />
            </button>

            <EnhancedNumberInput
                :model-value="modelValue"
                :min="min"
                :max="max"
                :step="step"
                class="appearance-none rounded-md text-center font-bold outline-none ring-inset ring-grind"
                :class="
                    'sm' === size
                        ? 'w-12 px-2 py-1 text-2xs ring-2 sm:w-16 sm:text-sm'
                        : 'w-20 px-5 py-3 ring-3 lg:w-24'
                "
                :invalid-class="invalidClass"
                aria-label="Quantity"
                data-cy="quantity-input-field"
                @update:model-value="$emit('update:model-value', $event)"
            />

            <button
                class="rounded-md ring-1 ring-inset ring-grind-200 transition hover:ring-grind"
                :class="
                    'sm' === size
                        ? 'px-1.5 hover:ring-2 sm:px-3'
                        : 'px-5 hover:ring-3'
                "
                aria-label="Increment quantity"
                data-cy="plus-btn"
                :disabled="maxDisabled"
                @click.prevent="onPlus"
            >
                <Icon
                    name="plus"
                    :class="{ 'text-2xs sm:text-sm': 'sm' === size }"
                />
            </button>
        </div>

        <InputHelpText v-if="helpText" :class="helpTextClass">
            {{ helpText }}
        </InputHelpText>

        <InputHelpText v-if="$slots.helpText">
            <slot name="helpText" />
        </InputHelpText>
    </div>
</template>

<script setup lang="ts">
import EnhancedNumberInput from '~/components/inputs/EnhancedNumberInput.vue';
import InputHelpText from '~/components/forms/components/InputHelpText.vue';

const emit = defineEmits(['update:model-value']);

const props = withDefaults(
    defineProps<{
        modelValue: number;
        min?: number;
        max?: number;
        step?: number;
        size?: 'lg' | 'sm';
        helpText?: string;
        helpTextClass?: string;
        invalidClass?: string;
    }>(),
    {
        min: undefined,
        max: undefined,
        step: 1,
        size: 'lg',
        helpText: undefined,
        helpTextClass: 'pl-4 text-sm',
        invalidClass: '!ring-red-700',
    },
);

const minDisabled = ref<boolean>(false);
const maxDisabled = ref<boolean>(false);

const onMinus = () => {
    maxDisabled.value = false;
    const newValue = props.modelValue - props.step;

    if (props.min !== null && props.min !== undefined && newValue < props.min) {
        return;
    }

    emit('update:model-value', newValue);
};

const onPlus = () => {
    minDisabled.value = false;
    const newValue = props.modelValue + props.step;

    if (props.max !== undefined) {
        maxDisabled.value = newValue === props.max;
    }

    if (props.max && newValue > props.max) {
        return;
    }

    emit('update:model-value', newValue);
};
</script>
